import proj4 from 'proj4';
import * as L from 'leaflet';
import * as turf from '@turf/turf';
import axios, { CancelTokenSource } from 'axios';

import NIService from 'services/NIService';
import {
  LeafletExtensionNIBaseLayer,
  NiLayerOptions,
} from './NIBaseClass/LeafletExtensionNIBaseLayer';
import { geoJsonLayers } from 'utils/Map/mapService';

export default class LeafletExtensionGeoJSONGridLayer extends LeafletExtensionNIBaseLayer {
  private _internalGridLayer: LeafletExtensionGeoJSONGridLayerInternal;

  constructor(options: NiLayerOptions) {
    super(options);

    this._internalGridLayer = new LeafletExtensionGeoJSONGridLayerInternal(
      this
    );
  }

  onAdd(map: L.Map): this {
    super.onAdd(map);

    if (this._internalGridLayer) {
      this._internalGridLayer.addTo(map);
    }
    return this;
  }

  onRemove(map: L.Map): this {
    if (this._internalGridLayer) {
      map.removeLayer(this._internalGridLayer);
    }
    return super.onRemove(map);
  }

  _handleLayerOutOfZoom(): void {
    if (this._map && this._map.hasLayer(this._internalGridLayer)) {
      this._map.removeLayer(this._internalGridLayer);
    }
  }

  _handleLayerWithinZoomBoundaries(): void {
    if (this._map && !this._map.hasLayer(this._internalGridLayer)) {
      this._internalGridLayer.addTo(this._map);
    }
  }
}

declare module 'leaflet' {
  interface GridLayer {
    _removeTile(tileKey: string): void; // Declare _removeTile in the GridLayer interface
  }
}

const EPSG4326 = 'EPSG:4326';
const EPSG2154 = 'EPSG:2154';

class LeafletExtensionGeoJSONGridLayerInternal extends L.GridLayer {
  private _internalGeoJsonLayer: L.GeoJSON;
  private tileCachePoly: any;
  private geoJsonFeaturesLoaded: number[];
  private niService: NIService;
  private cancelTokenPerTile: { [key: string]: CancelTokenSource };
  private niBaseLayerParent: LeafletExtensionNIBaseLayer;
  private loadedCachedData: boolean;

  constructor(niBaseLayerParent: LeafletExtensionNIBaseLayer) {
    super({});

    this.niBaseLayerParent = niBaseLayerParent;

    const layerColor = Object.values(
      niBaseLayerParent.niLayerOptions?.layerDefinition.LayerProperties
        ?.LegendValueToColorMapping ?? {}
    )[0];

    this._internalGeoJsonLayer = L.geoJson(null, {
      style: {
        color: layerColor || 'red',
        weight: 2,
        opacity: 0.6,
      },
      pointToLayer: (feature, latlng) =>
        L.circleMarker(latlng, {
          radius: 8,
          fillColor: layerColor || '#ff7800',
          color: '#000',
          weight: 1,
          opacity: 1,
          fillOpacity: this.niBaseLayerParent.niLayerOptions.opacity ?? 0.8,
        }),
      onEachFeature: (feature, layer) => {
        if (feature.properties) {
          const popupContent = Object.entries(feature.properties)
            .map(([key, value]) => `<strong>${key}:</strong> ${value ?? 'N/A'}`)
            .join('<br>');
          const popupOptions = {
            maxWidth: 300,
            className: 'custom-popup-geojson-layers',
          };
          layer.bindPopup(popupContent, popupOptions);
        }
      },
    });

    this.tileCachePoly = turf.polygon([]);
    this.geoJsonFeaturesLoaded = [];
    this.niService = new NIService();
    this.cancelTokenPerTile = {};
    this.loadedCachedData = false;
  }

  onAdd(map: L.Map): this {
    super.onAdd(map);

    if (this._internalGeoJsonLayer) {
      this._internalGeoJsonLayer.addTo(map);
    }
    return this;
  }

  onRemove(map: L.Map): this {
    if (this._internalGeoJsonLayer) {
      map.removeLayer(this._internalGeoJsonLayer);
    }
    return super.onRemove(map);
  }

  _removeTile(tileKey: string): void {
    const cancelToken = this.cancelTokenPerTile[tileKey];
    if (cancelToken) {
      try {
        cancelToken.cancel(`Cancelling API request for Tile [${tileKey}]`);
        delete this.cancelTokenPerTile[tileKey];
      } catch (e) {
        console.warn('API request cancellation failed:', e);
      }
    }
    super._removeTile(tileKey);
  }

  createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const tileKey = this._tileCoordsToKey(coords);
    const newTileDiv = document.createElement('div');
    const latLngBounds = this._tileCoordsToBounds(coords);
    const newTileRectangle = turf.polygon([
      [
        [latLngBounds.getSouthWest().lng, latLngBounds.getSouthWest().lat],
        [latLngBounds.getSouthEast().lng, latLngBounds.getSouthEast().lat],
        [latLngBounds.getNorthEast().lng, latLngBounds.getNorthEast().lat],
        [latLngBounds.getNorthWest().lng, latLngBounds.getNorthWest().lat],
        [latLngBounds.getSouthWest().lng, latLngBounds.getSouthWest().lat],
      ],
    ]);
    const newTileRectangleReduced = turf.buffer(newTileRectangle, -1, {
      units: 'meters',
    })!;

    if (
      !this.loadedCachedData &&
      this.niBaseLayerParent.niLayerOptions.layersLoadedFromCache?.includes(
        this.niBaseLayerParent.niLayerOptions.layerDefinition.LayerId
      )
    ) {
      this._internalGeoJsonLayer.addData(
        this.niBaseLayerParent.niLayerOptions.cachedData.data
      );
      this.geoJsonFeaturesLoaded =
        this.niBaseLayerParent.niLayerOptions.cachedData.data.map(
          (feature) => +feature.properties?.OBJECTID
        );

      this.tileCachePoly =
        this.niBaseLayerParent.niLayerOptions.cachedData.bounds;
      this.loadedCachedData = true;
    }

    if (turf.booleanWithin(newTileRectangleReduced, this.tileCachePoly)) {
      done(undefined, newTileDiv);
      return newTileDiv;
    }

    const tileEPSG2154 = this._tileCoordsToEPSG2154(latLngBounds);
    const requestParams = {
      layerId:
        this.niBaseLayerParent.niLayerOptions.layerDefinition.LayerId ?? '',
      viewPort: {
        minX: tileEPSG2154.minX,
        minY: tileEPSG2154.minY,
        maxX: tileEPSG2154.maxX,
        maxY: tileEPSG2154.maxY,
      },
    };

    const cancelTokenSource = axios.CancelToken.source();
    this.cancelTokenPerTile[tileKey] = cancelTokenSource;
    this.niBaseLayerParent.setNewLoadingState(true);

    this.niService
      .GetGeoJSONLayerData(requestParams, {
        cancelToken: cancelTokenSource.token,
      })
      .then((data) => {
        const parsedGeoJSONData = JSON.parse(data.GeoJSONData);
        parsedGeoJSONData.features = parsedGeoJSONData.features.filter(
          (feature) => {
            const featureId = +feature.properties?.OBJECTID;
            if (!featureId || !this.geoJsonFeaturesLoaded.includes(featureId)) {
              this.geoJsonFeaturesLoaded.push(featureId);
              return true;
            }
            return false;
          }
        );

        this._internalGeoJsonLayer.addData(parsedGeoJSONData);
        this.tileCachePoly = turf.union(
          turf.featureCollection([this.tileCachePoly, newTileRectangle])
        );

        delete this.cancelTokenPerTile[tileKey];

        done(undefined, newTileDiv);
      })
      .catch((error) => {
        delete this.cancelTokenPerTile[tileKey];
        if (axios.isCancel(error)) {
          done(undefined, newTileDiv);
        } else {
          done(error, newTileDiv);
        }
      })
      .finally(async () => {
        this.niBaseLayerParent.setNewLoadingState(
          Object.keys(this.cancelTokenPerTile).length > 0
        );

        // This is added to add the data of the layer to the localForage cache storage
        // So when we navigate from the map and return to it we will see the data instantly
        if (Object.keys(this.cancelTokenPerTile).length === 0) {
          let geoJsonFeatures: any[] = [];
          this._internalGeoJsonLayer.eachLayer((layer) => {
            const geoJsonLayer = layer as L.GeoJSON;
            geoJsonFeatures.push(geoJsonLayer.feature);
          });
          await geoJsonLayers({
            layer:
              this.niBaseLayerParent.niLayerOptions.layerDefinition.LayerId,
            data: geoJsonFeatures,
            bounds: this.tileCachePoly,
          });
        }
      });

    return newTileDiv;
  }

  private _tileCoordsToBounds(coords: L.Coords): L.LatLngBounds {
    const tileSize = this.getTileSize();
    const nwPoint = L.point(coords.x * tileSize.x, coords.y * tileSize.y);
    const sePoint = nwPoint.add([tileSize.x, tileSize.y]);

    const nw = this._map.unproject(nwPoint, coords.z);
    const se = this._map.unproject(sePoint, coords.z);

    return new L.LatLngBounds(nw, se);
  }

  private _tileCoordsToEPSG2154(latLngBounds: L.LatLngBounds) {
    const [minX, minY] = proj4(EPSG4326, EPSG2154, [
      latLngBounds.getSouthWest().lng,
      latLngBounds.getSouthWest().lat,
    ]);
    const [maxX, maxY] = proj4(EPSG4326, EPSG2154, [
      latLngBounds.getNorthEast().lng,
      latLngBounds.getNorthEast().lat,
    ]);
    return { minX, minY, maxX, maxY };
  }
}
